<script>
    import { onMount, onDestroy } from 'svelte';
    import Icon from '../icons/Icon.svelte';
    import { fetchStoreMpStatus, getStoreMpConnectUrl, disconnectStoreMp } from '../../services/api.js';

    export let selectedNiche = {};
    export let pixKey = '';
    export let pixKeyType = 'cpf';
    export let defaultDeliveryFee = 5.00;
    export let estimatedMinutes = 45;
    export let isSubmitting = false;
    export let onBack = () => {};
    export let onFinish = () => {};
    export let onNotify = (msg, type) => {};

    // Estado da Conexão Mercado Pago (Opcional)
    let mpStatus = { isConnected: false, userId: null };
    let isLoadingMp = false;
    let isConnectingMp = false;
    let isDisconnectingMp = false;

    function handleMpMessage(e) {
        if (e?.data?.type === 'MP_CONNECTED') {
            loadMpStatus();
            if (onNotify) onNotify('Conta Mercado Pago conectada com sucesso!', 'success');
        }
    }

    async function loadMpStatus() {
        isLoadingMp = true;
        try {
            const res = await fetchStoreMpStatus();
            if (res) {
                mpStatus = {
                    isConnected: Boolean(res.isConnected),
                    userId: res.userId
                };
            }
        } catch (_) {
            // Em caso de falha silenciosa, mantém status não conectado
        } finally {
            isLoadingMp = false;
        }
    }

    async function handleConnectMp() {
        isConnectingMp = true;
        try {
            const authUrl = await getStoreMpConnectUrl();
            if (!authUrl) throw new Error('Não foi possível gerar link de autorização do Mercado Pago.');

            const width = 600;
            const height = 750;
            const left = window.screen.width / 2 - width / 2;
            const top = window.screen.height / 2 - height / 2;
            const popup = window.open(
                authUrl,
                'mp_oauth_window',
                `width=${width},height=${height},top=${top},left=${left},scrollbars=yes,status=no`
            );

            if (!popup) {
                window.location.href = authUrl;
                return;
            }

            const pollTimer = setInterval(async () => {
                if (popup.closed) {
                    clearInterval(pollTimer);
                    await loadMpStatus();
                    isConnectingMp = false;
                }
            }, 1000);
        } catch (err) {
            if (onNotify) onNotify(`Erro ao conectar Mercado Pago: ${err.message}`, 'error');
            isConnectingMp = false;
        }
    }

    async function handleDisconnectMp() {
        isDisconnectingMp = true;
        try {
            await disconnectStoreMp();
            mpStatus = { isConnected: false, userId: null };
            if (onNotify) onNotify('Conta Mercado Pago desconectada.', 'info');
        } catch (err) {
            if (onNotify) onNotify(`Erro ao desconectar: ${err.message}`, 'error');
        } finally {
            isDisconnectingMp = false;
        }
    }

    onMount(() => {
        if (typeof window !== 'undefined') {
            window.addEventListener('message', handleMpMessage);
        }
        loadMpStatus();
    });

    onDestroy(() => {
        if (typeof window !== 'undefined') {
            window.removeEventListener('message', handleMpMessage);
        }
    });
</script>

<div class="finish-step-flow">
    <!-- CABEÇALHO DA ETAPA 4 -->
    <div class="step-header-text">
        <span class="step-badge">Etapa 4 de 4 · Recebimento & Operação</span>
        <h2 class="step-title">Como você deseja receber suas vendas?</h2>
        <p class="step-subtitle">
            {#if selectedNiche?.name}
                Tudo pronto para seu cardápio de <strong>{selectedNiche.name}</strong>! Configure sua chave PIX direta para receber na hora ou conecte o Mercado Pago para baixa automática.
            {:else}
                Configure sua chave PIX direta para vender em segundos ou conecte sua conta do Mercado Pago para confirmação automática.
            {/if}
        </p>
    </div>

    <div class="finish-sections-grid">
        <!-- BLOCO 1: FORMAS DE RECEBIMENTO (PIX & MERCADO PAGO) -->
        <div class="finish-card">
            <div class="card-title-row">
                <div class="card-icon-pill green">
                    <Icon name="check" size={18} color="#059669" />
                </div>
                <div>
                    <h3 class="card-title">Chave PIX do Estabelecimento</h3>
                    <p class="card-desc">O robô do WhatsApp gera o código PIX Copia e Cola para o cliente na hora do pedido.</p>
                </div>
            </div>

            <div class="card-body-fields">
                <div class="fields-2col">
                    <div class="field-item">
                        <label for="finishPixType">Tipo de Chave PIX</label>
                        <select id="finishPixType" bind:value={pixKeyType} class="clean-select">
                            <option value="cpf">CPF</option>
                            <option value="cnpj">CNPJ</option>
                            <option value="phone">Telefone (Celular)</option>
                            <option value="email">E-mail</option>
                            <option value="random">Chave Aleatória (EVP)</option>
                        </select>
                    </div>

                    <div class="field-item">
                        <label for="finishPixKey">Chave PIX Cadastrada</label>
                        <input
                            id="finishPixKey"
                            type="text"
                            bind:value={pixKey}
                            placeholder="Ex: 11987654321 ou loja@email.com"
                            class="clean-input"
                        />
                    </div>
                </div>

                <!-- INTEGRAÇÃO MERCADO PAGO CONNECT (1 CLIQUE / OPCIONAL) -->
                <div class="mp-integration-box {mpStatus.isConnected ? 'is-connected' : ''}">
                    <div class="mp-box-header">
                        <div class="mp-brand-pill">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <rect width="24" height="24" rx="6" fill="#009EE3"/>
                                <path d="M12 6C8.686 6 6 8.686 6 12C6 15.314 8.686 18 12 18C15.314 18 18 15.314 18 12C18 8.686 15.314 6 12 6Z" stroke="white" stroke-width="1.8"/>
                                <path d="M9.5 12L11.5 14L15 9.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <span class="mp-brand-name">Mercado Pago Connect</span>
                        </div>

                        {#if mpStatus.isConnected}
                            <span class="mp-badge-tag connected">
                                <span class="dot-green"></span> Conectado
                            </span>
                        {:else}
                            <span class="mp-badge-tag optional">
                                Opcional
                            </span>
                        {/if}
                    </div>

                    {#if mpStatus.isConnected}
                        <!-- ESTADO CONECTADO -->
                        <div class="mp-connected-content">
                            <div class="mp-connected-info">
                                <strong class="mp-connected-title">Conta vinculada com sucesso!</strong>
                                <p class="mp-connected-desc">Identificador: User #{mpStatus.userId || '---'}. Os pedidos PIX terão confirmação automática no painel e no WhatsApp.</p>
                            </div>
                            <button
                                type="button"
                                class="btn-mp-disconnect"
                                on:click={handleDisconnectMp}
                                disabled={isDisconnectingMp}
                            >
                                <span>{isDisconnectingMp ? 'Desconectando...' : 'Desconectar'}</span>
                            </button>
                        </div>
                    {:else}
                        <!-- ESTADO NÃO CONECTADO (OPCIONAL) -->
                        <div class="mp-disconnected-content">
                            <p class="mp-benefits-text">
                                Deseja que seus pedidos tenham <strong>baixa automática</strong> sem precisar conferir comprovantes manuais? Você pode conectar sua conta agora com 1 clique.
                            </p>

                            <div class="mp-action-row">
                                <button
                                    type="button"
                                    class="btn-mp-connect"
                                    on:click={handleConnectMp}
                                    disabled={isConnectingMp}
                                >
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                                    </svg>
                                    <span>{isConnectingMp ? 'Abrindo Mercado Pago...' : 'Conectar Mercado Pago'}</span>
                                </button>
                                <span class="mp-optional-hint">Você também pode conectar a qualquer momento em <em>Configurações > Pagamentos</em>.</span>
                            </div>
                        </div>
                    {/if}
                </div>
            </div>
        </div>

        <!-- BLOCO 2: OPERAÇÃO DE ENTREGA & TEMPO -->
        <div class="finish-card">
            <div class="card-title-row">
                <div class="card-icon-pill orange">
                    <Icon name="truck" size={18} color="#EA580C" />
                </div>
                <div>
                    <h3 class="card-title">Parâmetros de Entrega & Produção</h3>
                    <p class="card-desc">Estimativas padrão apresentadas aos seus clientes durante o atendimento.</p>
                </div>
            </div>

            <div class="card-body-fields">
                <div class="fields-2col">
                    <div class="field-item">
                        <label for="finishFee">Taxa de Entrega Padrão (R$)</label>
                        <input
                            id="finishFee"
                            type="number"
                            step="0.01"
                            min="0"
                            bind:value={defaultDeliveryFee}
                            class="clean-input"
                        />
                    </div>

                    <div class="field-item">
                        <label for="finishTime">Tempo Médio de Preparo (minutos)</label>
                        <input
                            id="finishTime"
                            type="number"
                            min="5"
                            bind:value={estimatedMinutes}
                            class="clean-input"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- BARRA INFERIOR DE FINALIZAÇÃO -->
    <div class="step-footer-actions">
        <button type="button" class="btn-back" on:click={onBack}>
            <Icon name="arrow-left" size={16} color="#64748B" />
            <span>Voltar aos complementos</span>
        </button>

        <button
            type="button"
            class="btn-finish-all"
            disabled={isSubmitting}
            on:click={onFinish}
        >
            <span>{isSubmitting ? 'Salvando configuração...' : 'Concluir e Começar a Vender'}</span>
            <Icon name="check" size={16} color="#FFFFFF" />
        </button>
    </div>
</div>

<style>
    .finish-step-flow {
        display: flex;
        flex-direction: column;
        gap: 22px;
        max-width: 820px;
        margin: 0 auto;
        width: 100%;
    }

    .step-header-text {
        text-align: left;
    }

    .step-badge {
        display: inline-block;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        color: #EA580C;
        background: #FFF7ED;
        padding: 3px 10px;
        border-radius: 6px;
        margin-bottom: 8px;
    }

    .step-title {
        font-size: 24px;
        font-weight: 800;
        color: #0F172A;
        letter-spacing: -0.4px;
        margin: 0 0 6px 0;
    }

    .step-subtitle {
        font-size: 14px;
        color: #64748B;
        margin: 0;
        line-height: 1.5;
    }

    .finish-sections-grid {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .finish-card {
        background: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: 12px;
        padding: 22px;
        display: flex;
        flex-direction: column;
        gap: 18px;
        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    }

    .card-title-row {
        display: flex;
        align-items: flex-start;
        gap: 12px;
    }

    .card-icon-pill {
        width: 36px;
        height: 36px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .card-icon-pill.green {
        background: #ECFDF5;
    }

    .card-icon-pill.orange {
        background: #FFF7ED;
    }

    .card-title {
        font-size: 15px;
        font-weight: 700;
        color: #0F172A;
        margin: 0 0 2px 0;
    }

    .card-desc {
        font-size: 13px;
        color: #64748B;
        margin: 0;
    }

    .card-body-fields {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .fields-2col {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 14px;
    }

    .field-item {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .field-item label {
        font-size: 12px;
        font-weight: 600;
        color: #475569;
    }

    .clean-input,
    .clean-select {
        height: 42px;
        padding: 0 12px;
        border-radius: 8px;
        background: #F8FAFC;
        border: 1px solid #CBD5E1;
        color: #0F172A;
        font-size: 13px;
        font-weight: 500;
        outline: none;
        transition: all 0.15s;
    }

    .clean-input:focus,
    .clean-select:focus {
        background: #FFFFFF;
        border-color: #FF6A00;
        box-shadow: 0 0 0 3px rgba(255, 106, 0, 0.12);
    }

    /* CARD INTEGRADO MERCADO PAGO */
    .mp-integration-box {
        background: #F8FAFC;
        border: 1px solid #E2E8F0;
        border-radius: 10px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        transition: all 0.2s ease;
    }

    .mp-integration-box.is-connected {
        background: #F0FDF4;
        border-color: #BBF7D0;
    }

    .mp-box-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .mp-brand-pill {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .mp-brand-name {
        font-size: 14px;
        font-weight: 700;
        color: #0F172A;
    }

    .mp-badge-tag {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        padding: 2px 8px;
        border-radius: 6px;
    }

    .mp-badge-tag.optional {
        background: #F1F5F9;
        color: #64748B;
        border: 1px solid #E2E8F0;
    }

    .mp-badge-tag.connected {
        background: #DCFCE7;
        color: #15803D;
        display: inline-flex;
        align-items: center;
        gap: 5px;
    }

    .dot-green {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #16A34A;
    }

    .mp-benefits-text {
        font-size: 13px;
        color: #475569;
        margin: 0;
        line-height: 1.5;
    }

    .mp-benefits-text strong {
        color: #0F172A;
    }

    .mp-action-row {
        display: flex;
        align-items: center;
        gap: 14px;
        flex-wrap: wrap;
    }

    .btn-mp-connect {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        height: 38px;
        padding: 0 16px;
        background: #009EE3;
        color: #FFFFFF;
        border: none;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s ease;
        box-shadow: 0 1px 3px rgba(0, 158, 227, 0.25);
    }

    .btn-mp-connect:hover:not(:disabled) {
        background: #0084BD;
        transform: translateY(-1px);
        box-shadow: 0 3px 8px rgba(0, 158, 227, 0.35);
    }

    .btn-mp-connect:disabled {
        opacity: 0.7;
        cursor: wait;
    }

    .mp-optional-hint {
        font-size: 12px;
        color: #64748B;
    }

    .mp-connected-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
    }

    .mp-connected-title {
        font-size: 13px;
        color: #166534;
        display: block;
        margin-bottom: 2px;
    }

    .mp-connected-desc {
        font-size: 12px;
        color: #15803D;
        margin: 0;
    }

    .btn-mp-disconnect {
        background: transparent;
        border: 1px solid #CBD5E1;
        border-radius: 6px;
        padding: 4px 10px;
        font-size: 12px;
        font-weight: 600;
        color: #64748B;
        cursor: pointer;
        transition: all 0.15s;
    }

    .btn-mp-disconnect:hover {
        background: #FEE2E2;
        border-color: #FCA5A5;
        color: #B91C1C;
    }

    /* FOOTER */
    .step-footer-actions {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-top: 14px;
        padding-bottom: 4px;
        border-top: 1px solid #E2E8F0;
        position: sticky;
        bottom: 0;
        background: #F8FAFC;
        z-index: 10;
    }

    .btn-back {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        height: 46px;
        padding: 0 18px;
        background: transparent;
        border: 1px solid #CBD5E1;
        border-radius: 10px;
        color: #475569;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .btn-back:hover {
        background: #F1F5F9;
        color: #0F172A;
    }

    .btn-finish-all {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        height: 48px;
        padding: 0 26px;
        background: #059669;
        color: #FFFFFF;
        font-size: 15px;
        font-weight: 700;
        border-radius: 10px;
        border: none;
        cursor: pointer;
        transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 0 2px 8px rgba(5, 150, 105, 0.3);
    }

    .btn-finish-all:hover:not(:disabled) {
        background: #047857;
        box-shadow: 0 4px 14px rgba(5, 150, 105, 0.4);
        transform: translateY(-1px);
    }

    .btn-finish-all:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    @media (max-width: 640px) {
        .fields-2col {
            grid-template-columns: 1fr;
        }

        .step-footer-actions {
            flex-direction: column-reverse;
            gap: 10px;
        }

        .btn-finish-all,
        .btn-back {
            width: 100%;
            justify-content: center;
        }

        .mp-connected-content {
            flex-direction: column;
            align-items: flex-start;
        }
    }
</style>
